import { useContext, useMemo } from 'react';
import { LoginStateContext } from '../../../../../Login/Contexts/LoginStateContext/LoginStateContext';
import {
  IValidateFn,
  mergeBusinessDataFormValues,
  parseText,
  IFieldValidations,
  createFormData,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { FakturaattestWrapperContext } from '../../../Rutiner/FakturaDel/Contexts/FakturaattestWrapperContext';
import { FakturaDelQuery_node_TEDLRFakturaDel } from '../../FakturaDel/Queries/_QueryTypes/FakturaDelQuery';
import {
  FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All,
  FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All__Associations_Kvittenser_All,
  FakturaDelType__Associations_Faktura__Associations_Fakturadel,
} from '../../FakturaDel/Queries/_QueryTypes/FakturaDelType';
import {
  getAktuellFakturaAttest,
  getMinAttestTyp,
} from '../../FakturaDel/Utils/FakturadelUtils';
import { AUTOMATICALLY_ADDED_VALUE_PREFIX } from '../../FakturaDel/Components/FakturaDelContextManager/FakturaDelContextManager';
import shortid from 'shortid';
import {
  LR_USE_KVITTERING,
  LR_KVITTERA_ENDAST_RIKTADE,
} from '../../FakturaDel/Consts/FakturaDelSysoptionConsts';
import { LRSysOptionsQuery_db_SYSOptions_All } from '../../FakturaDel/Queries/LRSysOptionQuery/_QueryTypes/LRSysOptionsQuery';
import { TEDLRAttestmeddelandeMOTTAGAR_TYPEnum } from '../../../../../../globalQueryTypes';
import { Verifikationdetails_node_TEDEKBokfver__Associations_Ursprungsverifikation__Associations_Handelser__Associations_Levfaktura__Associations_Attestmeddelande_All } from '../../../../Ekonomi/Rutiner/EkonomiskAnalys/Components/VerifikationModal/Queries/VerifikationQuery/_QueryTypes/Verifikationdetails';

export const getAttestantWithReserv = (
  fakturaDelar: FakturaDelType__Associations_Faktura__Associations_Fakturadel[],
  attestMeddelande: FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All
): string => {
  for (let i = 0; i < fakturaDelar.length; i++) {
    const fakturaDel = fakturaDelar[i];
    const fakturaAttester = fakturaDel._Associations?.Fakturaattest;
    if (fakturaAttester) {
      for (let j = 0; j < fakturaAttester.length; j++) {
        const fakturaAttest = fakturaAttester[j];
        const attestrattReserver =
          fakturaAttest._Associations?.Attestratt?._Associations?.Reserver_All;
        if (attestrattReserver) {
          for (let index = 0; index < attestrattReserver.length; index++) {
            const attestrattReserv = attestrattReserver[index];
            if (
              attestrattReserv &&
              attestrattReserv._Associations?.Reserv?.id ===
                attestMeddelande._Associations?.AvsandarAttestratt?.id
            ) {
              return fakturaAttest.Attestant ?? '';
            }
          }
        }
      }
    }
  }
  return '';
};

export const generateMessageID = () => {
  return AUTOMATICALLY_ADDED_VALUE_PREFIX + '_' + shortid.generate();
};

export const useValidateKvittering =
  (): IValidateFn<FakturaDelQuery_node_TEDLRFakturaDel> => {
    const loginStateContext = useContext(LoginStateContext);
    const fakturaattestContext = useContext(FakturaattestWrapperContext);

    const sysOptionActive: boolean = useMemo(() => {
      return (
        fakturaattestContext.sysOptions?.find(
          (option: LRSysOptionsQuery_db_SYSOptions_All) =>
            option.OptionKey === LR_USE_KVITTERING
        )?.OptionData === 'Y' 
      );
    }, [fakturaattestContext.sysOptions]);

    const onlyKvitteraDirectedMessages: boolean = useMemo(() => {
      return (
        fakturaattestContext.sysOptions?.find(
          (option: LRSysOptionsQuery_db_SYSOptions_All) =>
            option.OptionKey === LR_KVITTERA_ENDAST_RIKTADE
        )?.OptionData === 'Y' 
      );
    }, [fakturaattestContext.sysOptions]);

    return ({ data, values }) => {
      const formData: FakturaDelQuery_node_TEDLRFakturaDel = createFormData(
        values,
        data
      ) as any;
      if (!sysOptionActive) {
        return [];
      }

      let errors: IFieldValidations = [];

      const dataMergedWithChanges = mergeBusinessDataFormValues(
        parseText(formData),
        parseText(values ?? {})
      );

      const minAttestTyp = formData ? getMinAttestTyp(formData) : null;

      const minFakturaAttest = getAktuellFakturaAttest(
        dataMergedWithChanges?._Associations?.Fakturaattest ?? [],
        fakturaattestContext.reservForData,
        loginStateContext.sign,
        minAttestTyp
      );

      const attestMessages: FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All[] =
        (
          dataMergedWithChanges._Associations?.Faktura?._Associations as any
        )?.Attestmeddelande?.filter(
          (message: any) =>
            !values?._Associations?.Faktura_Values?._Associations?.Attestmeddelande_Delete_Values?.some(
              (deletedMessage: any) => deletedMessage.id === message.id
            )
        )?.filter(
          (
            message: FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All
          ) => !message.id.startsWith(AUTOMATICALLY_ADDED_VALUE_PREFIX)
        ) ?? [];

      if (attestMessages.length < 0) {
        return errors;
      }

      attestMessages.forEach(message => {
        const needsToKvitteraMessage = shouldUserKvitteraMessage(
          message,
          minFakturaAttest.aktuellFakturaAttest?._Associations?.Attestratt?.id,
          onlyKvitteraDirectedMessages
        );

        const isSender =
          (message?._Associations?.AvsandarAttestratt?.id ?? '') ===
            minFakturaAttest.aktuellFakturaAttest?._Associations?.Attestratt
              ?.id ||
          (message?.AvsandareSignatur?.toLocaleLowerCase() ?? '') ===
            loginStateContext.sign.toLocaleLowerCase();

        const assoc: any = message._Associations;

        const kvittens = assoc?.Kvittenser?.find(
          (
            kvittens: FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All__Associations_Kvittenser_All
          ) => {
            return (
              kvittens._Associations?.Attestratt?.id ===
              minFakturaAttest.aktuellFakturaAttest?._Associations?.Attestratt
                ?.id
            );
          }
        );

        if (!isSender && !kvittens && needsToKvitteraMessage) {
          errors.push({
            fieldName: 'messages',
            message:
              'Du har inte kvitterat meddelande från ' + message.Avsandare,
            type: 'Error',
          });
        }
      });

      return errors;
    };
  };

export const shouldUserKvitteraMessage = (
  message:
    | FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All
    | Verifikationdetails_node_TEDEKBokfver__Associations_Ursprungsverifikation__Associations_Handelser__Associations_Levfaktura__Associations_Attestmeddelande_All,
  aktuellFakturaAttestAttestrattId: string | null | undefined,
  onlyKvitteraDirectedMessages: boolean
) => {
  if (
    message?._Associations?.MottagarAttestratt?.id ===
    aktuellFakturaAttestAttestrattId
  ) {
    return true;
  } else if (
    message?._Associations?.MottagarAttestratt?.id == undefined &&
    (message.MOTTAGAR_TYP === TEDLRAttestmeddelandeMOTTAGAR_TYPEnum.ATTESTANT ||
      message.MOTTAGAR_TYP === TEDLRAttestmeddelandeMOTTAGAR_TYPEnum.ALLA)
  ) {
    return !onlyKvitteraDirectedMessages;
  } else {
    return false;
  }
};
